// ⛔ NO kalmstromShared4 IMPORT (2026-10-26): kUIText is loaded by the kEditableTable graph, which is kept free of
// kalmstromShared4 (kBrowserShared + `SetCoreUtils(ks)` at load would replace a non-ks product's error handler -
// WACommander's AppMoney test). Errors go through `getCoreUtils()` (= ks in a ks product); the UI language comes from
// `kUIText.LanguageHook`, which kalmstromShared4 sets at load to its own `UserLanguageLong`.
import { getCoreUtils, nz } from "../kReusableCoreTS/kCoreHandler";

/**
 * kUIText - figures and phrases for a SENTENCE, in the product's UI LANGUAGE.
 *
 * PROMOTED FROM SPArchive's SPAText (2026-10-01) because the same two needs were hand-rolled all over
 * the estate:
 *
 *   1. NUMBERS, PERCENTAGES, MONEY, TIMES AND DATES IN THE UI LANGUAGE, NOT THE BROWSER'S. A Swedish
 *      SharePoint read in an English-US browser said "Genomsökt 12:47 PM - Filer: 8,679" - Swedish
 *      words, American numbers. `Locale()` is the language kLNG speaks (`ks.UserLanguageLong`, set by
 *      `ks.UserLangeGetInfo`); the browser's is only the fallback when that is unset. Other products
 *      still call a bare `toLocaleString()` (MOSE MoseHtml.Thousands, ContractsManager CMFirstRun.Figure).
 *      SIZES follow the same language once a product sets `kByteSize.Locale = kUIText.Locale()`.
 *
 *   2. FILLING A PHRASE'S SLOTS. kLNG phrases carry "%1s", "%2s" (SPArchive, LISTONSITE) and shared
 *      components carry "{0}", "{1}" (kFacePile, kStepProgress, kWizard, kLinksEditor), each filled by
 *      its own `.replace(...)`. `String.replace` with a STRING replacement interprets "$&", "$'", "$1"
 *      in the VALUE - a link titled "Costs $& more" came out mangled (kLinksEditor). `Fill` splits and
 *      joins, so a value is always inserted verbatim, and it fills EVERY occurrence of a slot.
 *
 * Every method is pure and static: no DOM, one dependency (kCoreHandler).
 */
export class kUIText {
    /**
     * The UI language's BCP-47 tag ("sv-se"), or "" = the browser's. Set by kalmstromShared4 at load
     * (`kShared4UiLanguage.Long`, reads `ks.UserLanguageLong` live). Null (no ks loaded) = `DefaultLanguage`.
     */
    public static LanguageHook: (() => string) | null = null;
    /** The language with no hook - "en-us", what `ks.UserLanguageLong` starts as, so a product without ks formats as before. */
    public static DefaultLanguage: string = "en-us";

    /** The hooked UI language, or "". */
    private static Language = (): string => {
        // ERROR-SUPPRESSED-JUSTIFIED: no guard/catch here - only called inside Locale's / IsRightToLeft's try, which report.
        if (kUIText.LanguageHook === null) { return kUIText.DefaultLanguage; }
        return nz((kUIText.LanguageHook as () => string)()).trim();
    };

    /** The BCP-47 tag every figure is formatted in: the UI language ("sv-se"), else undefined = the browser's. */
    public static Locale = (): string | undefined => {
        if (getCoreUtils().KErrorMode === true) { return undefined; }
        try {
            const Tag: string = kUIText.Language();
            if (Tag === "") { return undefined; }
            return Tag;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kUIText.Locale");
            return undefined;
        }
    };

    /** The primary subtags written right to left: Arabic, Hebrew, Persian, Urdu (+ Dhivehi, Yiddish, Pashto). */
    public static RightToLeftLanguages: string[] = ["ar", "he", "fa", "ur", "dv", "yi", "ps"];

    /**
     * True when the UI language is written right to left - what a product sets `dir="rtl"` on its root
     * from. Decided from the language tag ("ar-sa" -> "ar"), never from the browser: an Arabic SharePoint
     * read in an English browser is still Arabic. No UI language set = false (left to right, as before).
     */
    public static IsRightToLeft = (): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            const Tag: string = kUIText.Language().toLowerCase();
            if (Tag === "") { return false; }
            return kUIText.RightToLeftLanguages.indexOf(Tag.split(/[-_]/)[0]) >= 0;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kUIText.IsRightToLeft");
            return false;
        }
    };

    /** A number with the UI language's thousand separator: 12345 -> "12,345" (en) / "12 345" (sv); MaxDecimals keeps up to that many decimals (12345.5, 6 -> "12 345,5"). */
    public static Count = (Value: number, MaxDecimals: number = 0): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return Value.toLocaleString(kUIText.Locale(), { maximumFractionDigits: MaxDecimals });
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kUIText.Count");
            return "";
        }
    };

    /** 77 -> "77%" (en) / "77 %" (sv) - a whole percent. */
    public static Percent = (Whole: number): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return new Intl.NumberFormat(kUIText.Locale(), { style: "percent", maximumFractionDigits: 0 }).format(Whole / 100);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kUIText.Percent");
            return "";
        }
    };

    /** 16.4 + "USD" -> "$16" / "16 US$" - whole units; an amount under 10 keeps its cents ("$0.40").
     *  Digits >= 0 forces that many decimals (2 for exact invoice amounts: "1 234,50 kr"); -1 = the rule above. */
    public static Money = (Amount: number, Currency: string, Digits: number = -1): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Decimals: number = Digits;
            if (Decimals < 0) { Decimals = 0; if (Math.abs(Amount) < 10) { Decimals = 2; } }
            return new Intl.NumberFormat(kUIText.Locale(), { style: "currency", currency: Currency,
                minimumFractionDigits: Decimals, maximumFractionDigits: Decimals }).format(Amount);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kUIText.Money");
            return "";
        }
    };

    /** "3 Jul" / "3 juli" - a day for a chart label or a "since". */
    public static DayMonth = (When: Date): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return When.toLocaleDateString(kUIText.Locale(), { day: "numeric", month: "short" });
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kUIText.DayMonth");
            return "";
        }
    };

    /** "07:40" / "7:40 AM" - the time part only. */
    public static Clock = (When: Date): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return When.toLocaleTimeString(kUIText.Locale(), { hour: "2-digit", minute: "2-digit" });
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kUIText.Clock");
            return "";
        }
    };

    /** "24 Sep 2026, 07:40" - a moment that may not be today. */
    public static DateTime = (When: Date): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return When.toLocaleString(kUIText.Locale(), { day: "numeric", month: "short", year: "numeric",
                hour: "2-digit", minute: "2-digit" });
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kUIText.DateTime");
            return "";
        }
    };

    /**
     * Fill a phrase's slots: "%1s of %2s" + ["3 GB", "25 TB"] -> "3 GB of 25 TB", and "{0} of {1}" the
     * same (the first value is %1s AND {0}). Every occurrence of a slot is filled; a slot with no value
     * stays as written. Values go in VERBATIM - "$&" in a value is two characters, not a back-reference.
     */
    public static Fill = (Phrase: string, Values: string[]): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Out: string = nz(Phrase);
            for (let Index: number = 0; Index < Values.length; Index++) {
                const Value: string = nz(Values[Index]);
                Out = Out.split("%" + String(Index + 1) + "s").join(Value);
                Out = Out.split("{" + String(Index) + "}").join(Value);
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kUIText.Fill");
            return "";
        }
    };

    /**
     * A number TYPED by the user, the inverse of Count / Money: NaN when blank or not a number, a decimal
     * comma read as a point ("12,5" -> 12.5) and spaces (also no-break, the Swedish thousands gap) dropped.
     */
    public static ParseNumber = (Text: string): number => {
        if (getCoreUtils().KErrorMode === true) { return NaN; }
        try {
            const Plain: string = nz(Text).split(" ").join("").split(" ").join("").replace(",", ".");
            if (Plain === "") { return NaN; }
            return Number(Plain);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kUIText.ParseNumber");
            return NaN;
        }
    };
}
